import _ from 'lodash';
import i18n from 'i18next';
import { Limitation, LimitationData } from 'core/limitation/Limitation';
import { rtbInventories } from 'containers/Limitations/LimitationSetting/rtbLimitationInventory';
import { getWeatherOptionI18nKey } from 'core/limitation/weatherOptions';
import { OPERATE, OPERATES } from 'enum/Operate';
import { overlayLabels } from 'utils/SelectOptionsUtils';

/**
 * Overlay current-locale labels onto every Limitation entry in `limitations`
 * whose `type` has a matching label map. Mutates `limitation.value` in place
 * (only when value is an array of SelectOptions). Types without a map are
 * left untouched. Used for outdoor limitations whose saved labels may be from
 * a different locale than the current session.
 */
export function overlayLimitationData(
  limitations: LimitationData,
  labelMaps: Map<string, Map<string | number, string>>
): void {
  for (const op of Object.keys(limitations)) {
    const ops = limitations[op] as Limitation[];
    if (!Array.isArray(ops)) continue;
    for (const limitation of ops) {
      const map = labelMaps.get(limitation.type);
      if (map && Array.isArray(limitation.value)) {
        limitation.value = overlayLabels(
          limitation.value as SelectOptions[],
          map
        );
      }
    }
  }
}

type AliasTuple = [name: string, alias: string];
const aliasTypeNameData: { isGroup: AliasTuple[]; notGroup: AliasTuple[] } = {
  isGroup: [['systemRecommendOutdoorDevice', 'adspace']],
  notGroup: [['customOutdoorDevice', 'adspace']]
};

const getTypeAlias = (isGroup: boolean, type: string) => {
  const isGroupKey: string = isGroup ? 'isGroup' : 'notGroup';
  const matchedAlias = aliasTypeNameData[isGroupKey].find((tuple: AliasTuple) =>
    tuple.includes(type)
  );
  if (!matchedAlias) {
    return type;
  }
  return matchedAlias[0] === type ? matchedAlias[1] : matchedAlias[0];
};

export const transformLimitationData = (
  useAliasName: boolean,
  op: string,
  data: Limitation
) => {
  if (!Array.isArray(data.value)) {
    return [
      {
        op: op,
        limits: data.value,
        isGroup: false,
        type: data.type
      }
    ];
  }
  // adFormat special case
  if (
    data.type === 'adFormat' &&
    data.value.find((limit) => limit.value === 'instl')
  ) {
    return [
      {
        op: op,
        limits: [
          {
            label: '蓋板',
            value: '1'
          }
        ],
        isGroup: false,
        type: 'instl'
      }
    ];
  }
  const limits = [...data.value];
  const groupData = _.remove(limits, (value: any) => value.isGroup === true);
  const getLimitLabel = (type: string, value: SelectOptions) => {
    if (type === 'adspace') {
      return value.label.replace(`${value.value},`, '');
    }
    return type === 'contentcat'
      ? i18n.t(`limitation.contentCategory.${value.label}`)
      : value.label;
  };

  const type = useAliasName ? getTypeAlias(false, data.type) : data.type;
  let result =
    limits.length > 0
      ? [
          {
            op: op,
            limits: limits.map((value) => {
              // [PMAX-4467] contentcat should save translated sentence to make PMAX1.0 to show correct value.
              return {
                label: getLimitLabel(type, value),
                value: value.value,
                // 固定門市：有掛 spaceId 的項目（被固定的門市）存檔時一併帶回，
                // 讓後端能對回那一台版位。其他限制項沒有 spaceId，不受影響。
                ...((value as SelectOptions).spaceId != null
                  ? { spaceId: (value as SelectOptions).spaceId }
                  : {})
              };
            }),
            isGroup: false,
            type
          }
        ]
      : [];

  if (groupData.length > 0) {
    const type = useAliasName ? getTypeAlias(true, data.type) : data.type;
    result.push({
      op: op,
      limits: groupData.map((value) => {
        // [PMAX-4467] contentcat should save translated sentence to make PMAX1.0 to show correct value.
        return {
          label: getLimitLabel(type, value),
          value: value.value
        };
      }),
      isGroup: true,
      type
    });
  }
  return result;
};

export function toServerStructure(
  limitations,
  useAliasName = false,
  filterTA: { [key: string]: string[] } = {
    other: ['dealId'],
    preferred: ['age_min', 'age_max'],
    include: ['age_min', 'age_max']
  }
) {
  const otherFilter = _.defaultTo(filterTA.other, []);
  const otherLimitations = _.filter(
    _.defaultTo(limitations.other, []),
    (limitation: Limitation) => !otherFilter.includes(limitation.type)
  );

  const includeFilter = _.defaultTo(filterTA.include, []);
  const includeLimitations = _.filter(
    _.defaultTo(limitations.include, []),
    (limitation: Limitation) => !includeFilter.includes(limitation.type)
  );

  const preferredFilter = _.defaultTo(filterTA.preferred, []);
  const preferredLimitations = _.filter(
    _.defaultTo(limitations.preferred, []),
    (limitation: Limitation) => !preferredFilter.includes(limitation.type)
  );

  const _transformLimitationData = _.partial(
    transformLimitationData,
    useAliasName
  );

  let result = _.compact(
    _.flatten(
      _.concat(
        includeLimitations.map((value) => {
          return _transformLimitationData('inc', value);
        }),
        _.defaultTo(limitations.exclude, []).map((value) => {
          return _transformLimitationData('exc', value);
        }),
        preferredLimitations.map((value) => {
          return _transformLimitationData('Preferred', value);
        }),
        _.defaultTo(limitations.nonPreferred, []).map((value) => {
          return _transformLimitationData('NonPreferred', value);
        }),
        // dealId
        otherLimitations
          .filter((value) => {
            return value.type !== 'testMode';
          })
          .map((value) => {
            if (value.type === 'dealId') {
              return _transformLimitationData('inc', value);
            } else {
              return _transformLimitationData(value.op, value);
            }
          })
      )
    )
  );
  return result;
}

export function getCustomGeofenceLabel(value: string) {
  const [latitude, longitude, radius] = value
    .toString()
    .replace(' ', '')
    .split(',');
  return `(${latitude},${longitude}), ${radius}KM`;
}

export function getLimitationContentLabel(type, limitation) {
  // 天氣選項的值是氣象代碼區間，組不出 i18n key，得查固定選項表才翻得出來。
  const weatherKey = getWeatherOptionI18nKey(type, String(limitation.value));
  if (weatherKey) {
    return i18n.t(weatherKey);
  }
  const i18nValue = limitation.value
    .toString()
    .toLowerCase()
    .replace(/-|\s/g, '_');
  const inventoryData: any = rtbInventories[type]
    ? rtbInventories[type]
    : { i18nPrefix: 'limitation.labels' };

  const i18nKey = `${inventoryData.i18nPrefix}.${i18nValue}`;
  if (inventoryData.i18nPrefix && i18n.exists(i18nKey)) {
    return i18n.t(i18nKey);
  }
  if (type === 'customGeofence') {
    return getCustomGeofenceLabel(limitation.value);
  }
  return limitation.label;
}

export function getLimitationAddonDependency(limitationType, limitationName) {
  const addonMap = {
    geography: `${limitationType}_country`,
    carrier: `${limitationType}_carrier`,
    device: `${limitationType}_device`,
    os: `${limitationType}_device`,
    segment: `${limitationType}_target_audience`,
    tenmaxAudience: `${limitationType}_tenmax_audience`,
    contentcat: `${limitationType}_content_category`,
    unicornkeywords: `${limitationType}_keyword`,
    adx: `${limitationType}_adexchange`,
    spaceType: `${limitationType}_spaceType`,
    adspace: `${limitationType}_asiamax_space`,
    contentlang: `${limitationType}_content_language`,
    adcat: `${limitationType}_space_category`,
    domain: `${limitationType}_domain`
  };
  return addonMap[limitationName];
}

export function wrapLimitationsFromServer(
  limitations,
  dealIds?: string[],
  ageMin?: number,
  ageMax?: number,
  isTestMode?: boolean,
  useAliasName = false
) {
  const limitationMap = {
    inc: {},
    exc: {},
    Preferred: {},
    NonPreferred: {},
    other: {}
  };
  limitations.forEach((limitation) => {
    const limits = _.get(limitation, 'limits', []);
    const isGroup = _.get(limitation, 'isGroup');
    const type = useAliasName
      ? getTypeAlias(isGroup, _.get(limitation, 'type'))
      : _.get(limitation, 'type');
    const wrappedLimitation = {
      op: _.get(limitation, 'op'),
      value: Array.isArray(limits)
        ? limits.map((limits) => {
            // 固定門市：載入時保留後端存下的 spaceId，才能在存回時原樣帶出、
            // 並用它對出該台版位的狀態。沒有的項目照舊。
            const spaceId = _.get(limits, 'spaceId');
            return {
              label: _.get(limits, 'label'),
              value: _.get(limits, 'value'),
              isGroup,
              ...(spaceId !== undefined && spaceId !== null
                ? { spaceId: spaceId.toString() }
                : {})
            };
          })
        : limits,
      type
    };
    if (type === 'dealId') {
      limitationMap['other'][type] = wrappedLimitation;
    } else if (type === 'instl') {
      // transfer legacy code
      limitationMap[limitation.op]['adFormat'] = {
        op: limitation.op,
        value: [
          {
            label: 'instl',
            value: 'instl',
            isGroup: false
          }
        ],
        type: 'adFormat'
      };
    } else if (type in limitationMap[limitation.op]) {
      limitationMap[limitation.op][type].value = _.concat(
        limitationMap[limitation.op][type].value,
        wrappedLimitation.value
      );
    } else {
      limitationMap[limitation.op][type] = wrappedLimitation;
    }
  });

  const wrappedLimitations: LimitationData = {
    include: Object.values(limitationMap['inc']),
    exclude: Object.values(limitationMap['exc']),
    preferred: Object.values(limitationMap['Preferred']),
    nonPreferred: Object.values(limitationMap['NonPreferred']),
    other: Object.values(limitationMap['other'])
  };

  const ageOptValue = _.find(
    wrappedLimitations.include,
    (limitation: Limitation) => limitation.type === 'age'
  )
    ? 'include'
    : 'preferred';

  ageMin &&
    wrappedLimitations[ageOptValue].push({
      op: ageOptValue === 'include' ? 'inc' : 'Preferred',
      value: ageMin,
      type: 'age_min'
    });

  ageMax &&
    wrappedLimitations[ageOptValue].push({
      op: ageOptValue === 'include' ? 'inc' : 'Preferred',
      value: ageMax,
      type: 'age_max'
    });

  const dealIdTA = wrappedLimitations.other.find((ta) => ta.type === 'dealId');
  !dealIdTA &&
    wrappedLimitations.other.push({
      op: 'inc',
      value: dealIds
        ? dealIds.map((dealId) => {
            return {
              label: dealId,
              value: dealId
            };
          })
        : [],
      type: 'dealId'
    });

  wrappedLimitations.other.push({
    op: 'inc',
    value: isTestMode ? 1 : 0,
    type: 'testMode'
  });

  return wrappedLimitations;
}

export function getLimitationOp(serverOp: string) {
  const opMap: Record<string, OPERATES> = {
    inc: OPERATE.INCLUDE,
    exc: OPERATE.EXCLUDE,
    Preferred: OPERATE.PREFERRED,
    NonPreferred: OPERATE.NONPREFERRED
  };
  return opMap[serverOp] || 'other';
}

export function isTestMode(limitations: LimitationData) {
  const testModeLimitation = limitations.other?.find(
    (item: any) => item.type === 'testMode'
  );
  if (testModeLimitation !== undefined) {
    return testModeLimitation.value === 1;
  }
  return false;
}
